/**
 * LinkInvoiceCustomerDialog
 *
 * Toont wanneer een medewerker een reminder wil versturen voor een factuur
 * ZONDER gekoppelde klant (bv. Josje Tapia case). Presenteert:
 *   - Automatische suggesties uit `_find_customer_matches` (e-mail, telefoon,
 *     bedrijfsnaam, naam+e-mail/telefoon combinaties)
 *   - Handmatige zoekactie op alle klanten indien geen suggestie past
 *   - Actie "Nieuwe klant aanmaken" (optioneel — deep-link naar CRM)
 *
 * Na succesvolle koppeling:
 *   - Toast met bevestiging
 *   - onLinked(customer_id) callback zodat de parent (bv. BetalingsopvolgingTab)
 *     de Composer direct kan openen met de nieuw gekoppelde klant.
 */
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { toast } from "sonner";
import { Loader2, Link as LinkIcon, Search, User, Users, Sparkles } from "lucide-react";
import { adminApi, formatApiError } from "../../../context/AuthContext";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../../ui/dialog";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Badge } from "../../ui/badge";

const CONF_META = {
  high: { label: "Zeer betrouwbaar", bg: "bg-emerald-100", text: "text-emerald-800" },
  medium: { label: "Redelijk betrouwbaar", bg: "bg-amber-100", text: "text-amber-800" },
  low: { label: "Suggestie", bg: "bg-gray-100", text: "text-gray-700" },
};

const MATCH_TYPE_LABEL = {
  email_exact: "Zelfde e-mailadres",
  phone_exact: "Zelfde telefoon/WhatsApp",
  company_exact: "Zelfde bedrijfsnaam",
  name_and_email: "Naam + e-mail",
  name_and_phone: "Naam + telefoon",
  manual: "Handmatig",
};

export default function LinkInvoiceCustomerDialog({
  open,
  onOpenChange,
  invoice,       // {id, invoice_number, customer_name, customer_email}
  onLinked,      // (customer_id, customer_snapshot) => void
}) {
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [linking, setLinking] = useState(false);

  const invoiceId = invoice?.id;
  const invoiceNumber = invoice?.invoice_number;
  const customerHint = useMemo(() => {
    const bits = [];
    if (invoice?.customer_name) bits.push(invoice.customer_name);
    if (invoice?.customer_email) bits.push(invoice.customer_email);
    return bits.join(" · ") || "onbekende klant";
  }, [invoice]);

  const loadSuggestions = useCallback(async () => {
    if (!invoiceId) return;
    setLoadingSuggestions(true);
    try {
      const r = await adminApi.get(
        `/admin/sales-invoices/${invoiceId}/customer-matches`,
      );
      setSuggestions(r.data?.suggestions || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoadingSuggestions(false);
    }
  }, [invoiceId]);

  useEffect(() => {
    if (open) {
      setSearchTerm("");
      setSearchResults([]);
      loadSuggestions();
    }
  }, [open, loadSuggestions]);

  const runSearch = async (q) => {
    if (!q || q.trim().length < 2) {
      setSearchResults([]);
      return;
    }
    setSearching(true);
    try {
      const r = await adminApi.get("/admin/customers", {
        params: { q: q.trim(), limit: 25 },
      });
      // Endpoint retourneert een raw array (geen wrapper)
      const items = Array.isArray(r.data) ? r.data : (r.data?.customers || r.data?.items || []);
      setSearchResults(Array.isArray(items) ? items : []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSearching(false);
    }
  };

  // Debounced search
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => runSearch(searchTerm), 300);
    return () => clearTimeout(t);
  }, [searchTerm, open]);

  const handleLink = async (customerId, matchType = "manual", confidence = null) => {
    if (!invoiceId || !customerId) return;
    setLinking(true);
    try {
      const r = await adminApi.post(
        `/admin/sales-invoices/${invoiceId}/link-customer`,
        { customer_id: customerId, match_type: matchType, confidence },
      );
      const snap = r.data?.linked_customer || null;
      toast.success(`Klant gekoppeld aan factuur ${invoiceNumber || ""}`.trim());
      onLinked?.(customerId, snap);
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLinking(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-2xl rounded-2xl max-h-[85vh] flex flex-col"
        data-testid="link-invoice-customer-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-brand-900">
            <LinkIcon className="w-5 h-5" />
            Klant koppelen aan factuur {invoiceNumber || ""}
          </DialogTitle>
        </DialogHeader>

        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-sm text-amber-900">
          <div className="font-semibold mb-1">Deze factuur is nog niet gekoppeld aan een klant.</div>
          <div className="text-xs">
            Op de factuur staat: <strong>{customerHint}</strong>. Kies hieronder de juiste klant zodat
            reminders, betaallinks en de communicatiehistorie correct worden bijgehouden.
          </div>
        </div>

        <div className="flex-1 overflow-y-auto pr-1">
          {/* Suggesties */}
          <section className="mb-4">
            <h4 className="text-xs font-semibold text-gray-500 mb-2 flex items-center gap-1">
              <Sparkles className="w-3.5 h-3.5" /> Automatische suggesties
            </h4>
            {loadingSuggestions ? (
              <div className="flex items-center gap-2 text-sm text-gray-500 p-4 border rounded-xl">
                <Loader2 className="w-4 h-4 animate-spin" /> Suggesties zoeken…
              </div>
            ) : suggestions.length === 0 ? (
              <div className="p-4 border rounded-xl text-sm text-gray-500 italic bg-gray-50" data-testid="link-invoice-no-suggestions">
                Geen automatische suggesties gevonden. Zoek handmatig hieronder.
              </div>
            ) : (
              <div className="space-y-2">
                {suggestions.map((s) => (
                  <SuggestionRow
                    key={s.customer_id}
                    suggestion={s}
                    onLink={() => handleLink(s.customer_id, s.match_type, s.confidence)}
                    disabled={linking}
                  />
                ))}
              </div>
            )}
          </section>

          {/* Handmatige zoekactie */}
          <section>
            <h4 className="text-xs font-semibold text-gray-500 mb-2 flex items-center gap-1">
              <Search className="w-3.5 h-3.5" /> Handmatig zoeken
            </h4>
            <Input
              placeholder="Zoek op naam, e-mail, bedrijf of telefoon…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="rounded-full h-9"
              data-testid="link-invoice-search-input"
            />
            {searching && (
              <div className="flex items-center gap-2 text-xs text-gray-500 mt-2">
                <Loader2 className="w-3 h-3 animate-spin" /> Zoeken…
              </div>
            )}
            {searchResults.length > 0 && (
              <div className="mt-2 space-y-1 max-h-[240px] overflow-y-auto" data-testid="link-invoice-search-results">
                {searchResults.map((c) => {
                  const full = [c.first_name, c.last_name].filter(Boolean).join(" ");
                  return (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => handleLink(c.id, "manual", "low")}
                      disabled={linking}
                      className="w-full text-left rounded-xl border border-gray-200 hover:border-brand-500 hover:bg-brand-50/40 transition p-2 text-sm flex items-center gap-2"
                      data-testid={`link-invoice-search-result-${c.id}`}
                    >
                      <User className="w-4 h-4 text-brand-500 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-brand-900 truncate">
                          {full || c.company_name || c.email || c.id}
                        </div>
                        <div className="text-xs text-gray-500 truncate">
                          {[c.company_name, c.email, c.phone || c.whatsapp]
                            .filter(Boolean).join(" · ") || "—"}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
            {searchTerm && !searching && searchResults.length === 0 && (
              <div className="text-xs text-gray-500 italic mt-2 p-2">
                Geen klanten gevonden voor deze zoekterm.
              </div>
            )}
          </section>
        </div>

        <DialogFooter className="pt-4 border-t border-gray-200">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="rounded-full"
            disabled={linking}
          >
            Annuleren
          </Button>
          <Button
            variant="outline"
            onClick={() => window.open("/admin?section=customers&new=1", "_blank", "noopener")}
            className="rounded-full"
            data-testid="link-invoice-create-customer-btn"
          >
            <Users className="w-3.5 h-3.5 mr-1" /> Nieuwe klant aanmaken (in nieuw tabblad)
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SuggestionRow({ suggestion, onLink, disabled }) {
  const conf = CONF_META[suggestion.confidence] || CONF_META.low;
  const typeLabel = MATCH_TYPE_LABEL[suggestion.match_type] || suggestion.match_type;
  return (
    <div
      className="border border-gray-200 rounded-xl p-3 hover:border-brand-500 hover:bg-brand-50/30 transition"
      data-testid={`link-invoice-suggestion-${suggestion.customer_id}`}
    >
      <div className="flex items-center gap-2 mb-1">
        <User className="w-4 h-4 text-brand-500" />
        <div className="font-semibold text-brand-900 flex-1 min-w-0 truncate">
          {suggestion.name || suggestion.company || suggestion.email || "Onbekende klant"}
        </div>
        <Badge className={`${conf.bg} ${conf.text} rounded-full text-[10px]`}>
          {conf.label}
        </Badge>
      </div>
      <div className="text-xs text-gray-500 mb-2 flex flex-wrap gap-x-2 gap-y-0.5">
        {suggestion.company && <span>🏢 {suggestion.company}</span>}
        {suggestion.email && <span>✉️ {suggestion.email}</span>}
        {suggestion.phone && <span>📞 {suggestion.phone}</span>}
      </div>
      <div className="flex items-center gap-2">
        <Badge className="bg-brand-50 text-brand-900 border border-brand-100 rounded-full text-[10px] font-normal">
          {typeLabel}
        </Badge>
        <div className="flex-1" />
        <Button
          size="sm"
          onClick={onLink}
          disabled={disabled}
          className="rounded-full h-7 bg-brand-500 hover:bg-brand-500/90 text-white text-xs"
          data-testid={`link-invoice-suggestion-btn-${suggestion.customer_id}`}
        >
          Kies deze klant
        </Button>
      </div>
    </div>
  );
}
